export type EventRecurrenceFrequency =
  | "daily"
  | "weekly"
  | "biweekly"
  | "monthly";

export type EventRecurrence = {
  frequency: EventRecurrenceFrequency;
  interval?: number;
  count?: number;
  until?: string;
};

export const MAX_EVENT_SERIES_OCCURRENCES = 52;

export function formatLocalDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Calendar day in local time. Date-only strings stay on that day (no UTC shift). */
export function toLocalDay(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const day = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(day.getTime()) ? null : day;
}

/**
 * First end date that includes a second occurrence.
 * Matches backend expansion: daily +1, weekly +7, biweekly +14, monthly +1 month.
 */
export function earliestSeriesEndDate(
  start: Date | string | null | undefined,
  frequency: string | undefined,
): Date | null {
  const day = toLocalDay(start);
  if (!day || !frequency || frequency === "none") return null;
  if (frequency === "daily") day.setDate(day.getDate() + 1);
  else if (frequency === "weekly") day.setDate(day.getDate() + 7);
  else if (frequency === "biweekly") day.setDate(day.getDate() + 14);
  else if (frequency === "monthly") day.setMonth(day.getMonth() + 1);
  else return null;
  return day;
}

export function summarizeEventRecurrence(
  recurrence: EventRecurrence | undefined,
  startDate: Date | null
): string {
  if (!recurrence?.frequency || !startDate) return "";
  const freqLabel: Record<EventRecurrenceFrequency, string> = {
    daily: "Daily",
    weekly: "Weekly",
    biweekly: "Every 2 weeks",
    monthly: "Monthly",
  };
  const base = freqLabel[recurrence.frequency];
  if (recurrence.count) {
    return `${base} · ${recurrence.count} times`;
  }
  if (recurrence.until) {
    return `${base} · until ${recurrence.until}`;
  }
  return base;
}

export function buildEventRecurrencePayload(
  frequency: string,
  untilDate: Date | null,
): EventRecurrence | undefined {
  if (!frequency || frequency === "none") return undefined;
  const recurrence: EventRecurrence = {
    frequency: frequency as EventRecurrenceFrequency,
  };
  if (untilDate) {
    recurrence.until = formatLocalDate(untilDate);
  }
  return recurrence;
}
